import React, { useContext, useRef } from "react";

import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useTranslation } from "react-i18next";
import { AiOutlineSwap } from "react-icons/ai";
import { useHistory } from "react-router-dom";
import { MembersGender } from "Util/enums/members";
import * as yup from "yup";

import Button, { Color } from "../../../../../../../../../../components/Form/Button";
import SkeletonComponent from "../../../../../../../../../../components/Generic/Skeleton";
import { Member } from "../../../../../../../../../../models/members/member";
import { MemberStaff } from "../../../../../../../../../../models/members/member.staff";
import { MemberUser } from "../../../../../../../../../../models/members/member.user";
import { MembersType } from "../../../../../../../../../../util/enums";
import { MemberContext } from "../../../context";
import { Wrapper } from "../../../styles";
import { Steps } from "../../Steps";
import ConfigureSubcontractorModal, {
  ConfigureSubcontractorHandler,
} from "./ConfigureSubcontractor";
import ConvertIntoModal, { ConvertIntoHandler } from "./ConvertInto";
import {
  ButtonWrapper,
  DetailsHeader,
  HeaderMessage,
  SectionTitle,
  SectionWrapper,
} from "./styles";

export const memberGenderOptions = [
  {
    value: MembersGender.MALE,
    label: "male",
  },
  {
    value: MembersGender.FEMALE,
    label: "female",
  },
  {
    value: MembersGender.NOT_BINARY,
    label: "nonBinary",
  },
  {
    value: MembersGender.OTHER,
    label: "preferNotToSay",
  },
];

type Props = {
  isLoading?: boolean;
  errors?: yup.ValidationError[];
};

export const DetailsTab: React.FC<Props> = ({ isLoading = false, errors = [] }) => {
  const { t: translate } = useTranslation();
  const { member, setMember, setMemberHasShifted } = useContext(MemberContext);
  const { location } = useHistory<{ isView: boolean }>();
  const configureAsSubcontractorRef = useRef<ConfigureSubcontractorHandler>(null);
  const convertIntoRef = useRef<ConvertIntoHandler>(null);

  const getSubcontractorToastMessage = (isSubcontractor: boolean) => {
    if (!isSubcontractor) {
      return translate("memberWillChangeFromSubcontractorToOwnAfterSaving");
    }

    return translate("memberWillChangeFromOwnToSubcontractorAfterSaving");
  };

  const getStaffToastMessage = (isStaff: boolean) => {
    if (!isStaff) {
      return translate("memberWillChangeFromStaffToUserAfterSaving");
    }

    return translate("memberWillChangeFromUserToStaffAfterSaving");
  };

  const handleConvertMemberToastMessage = (member: Member, isConvertingSubcontractor?: boolean) => {
    if (isConvertingSubcontractor) {
      return getSubcontractorToastMessage(!!member?.subcontractorId);
    }

    return getStaffToastMessage(
      member?.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase()
    );
  };

  return (
    <div>
      <DetailsHeader>
        <SkeletonComponent isLoading={isLoading}>
          <>
            <div>
              <HeaderMessage hasSubcontractor={!!member?.subcontractorId}>
                {translate("thisMemberIsA")}{" "}
                <span>
                  {member?.subcontractor
                    ? `${translate("subcontractorFor")} ‘${member?.subcontractor?.businessName}’`
                    : translate("own")}
                </span>{" "}
                {translate("andIsDefinedAsA")} {"‘"}
                <b>{member?.typeOfMember?.toLowerCase()}</b>
                {"’"}
              </HeaderMessage>
            </div>
            <ButtonWrapper>
              <Button
                disabled={location.state?.isView}
                onClick={() => configureAsSubcontractorRef?.current?.onOpenModal(member as Member)}
              >
                {translate("configureAsSubcontractor")}
              </Button>
              <Button
                disabled={location.state?.isView}
                onClick={() => convertIntoRef?.current?.onOpenModal(member as Member)}
                customColor={Color.SECONDARY_DARK}
                startIcon={<AiOutlineSwap />}
              >
                {translate("convertInto")}{" "}
                {member?.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase()
                  ? translate("user").toLowerCase()
                  : translate("staff").toLowerCase()}
              </Button>
            </ButtonWrapper>
          </>
        </SkeletonComponent>
      </DetailsHeader>

      <Wrapper>
        <SectionWrapper>
          <SkeletonComponent isLoading={isLoading}>
            <>
              <div>
                <SectionTitle>{translate("personalDetails")}</SectionTitle>
              </div>
              <Steps.DETAILS errors={errors} />
            </>
          </SkeletonComponent>
        </SectionWrapper>

        <SectionWrapper>
          <SkeletonComponent isLoading={isLoading}>
            <>
              <div>
                <SectionTitle>
                  {member?.typeOfMember?.toLowerCase() === MembersType.USER?.toLowerCase()
                    ? translate("userDetails")
                    : translate("staffInformation")}
                </SectionTitle>
              </div>
              {member?.typeOfMember?.toLowerCase() === MembersType.USER?.toLowerCase() ? (
                <Steps.USER_DETAILS errors={errors} />
              ) : (
                <Steps.STAFF_DETAILS errors={errors} />
              )}
            </>
          </SkeletonComponent>
        </SectionWrapper>

        <SectionWrapper>
          <SkeletonComponent isLoading={isLoading}>
            <>
              <div>
                <SectionTitle>{translate("additional")}</SectionTitle>
              </div>
              <Steps.ADDITIONAL errors={errors} />
            </>
          </SkeletonComponent>
        </SectionWrapper>

        <SectionWrapper>
          <SkeletonComponent isLoading={isLoading}>
            <>
              <div>
                <SectionTitle>{translate("activeLoginSessions")}</SectionTitle>
              </div>
              <Steps.LOGIN_SESSIONS />
            </>
          </SkeletonComponent>
        </SectionWrapper>
      </Wrapper>

      <ConfigureSubcontractorModal
        ref={configureAsSubcontractorRef}
        onSubmit={(currentMember: Member) => {
          if (currentMember?.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase()) {
            setMember(currentMember as MemberStaff);
          } else {
            setMember(currentMember as MemberUser);
          }
          NotificationToast.success(handleConvertMemberToastMessage(currentMember, true), {
            id: "convertMemberToSubContractorOrOwn",
          });
          configureAsSubcontractorRef.current?.onCloseModal();
        }}
      />
      <ConvertIntoModal
        ref={convertIntoRef}
        onSubmit={(currentMember: Member) => {
          setMemberHasShifted?.(true);

          if (currentMember?.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase()) {
            setMember(currentMember as MemberStaff);
          } else {
            setMember(currentMember as MemberUser);
          }
          NotificationToast.success(handleConvertMemberToastMessage(currentMember), {
            id: "convertMemberToStaffOrUser",
          });
          convertIntoRef.current?.onCloseModal();
        }}
      />
    </div>
  );
};
